import { FC } from "react";
import { Stack, Typography, IconButton, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { background, text as textColor, primary, divider, icon } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded, spacing } from "@/theme/variables";
import { IconSetting, IconChevronLeft, IconChevronRight } from "@/assets/icons";
import { CircularStudyProgress } from "@/features/study-time/components/CircularStudyProgress";
import { WeeklyBarChart } from "@/features/study-time/components/WeeklyBarChart";
import { StudyTimeStatItem } from "@/features/study-time/components/StudyTimeStatItem";
import { WeeklyStudyTimeSkeleton } from "@/features/dashboard/components/skeletons/WeeklyStudyTimeSkeleton";
import { useWeeklyStudyTime } from "@/infrastructure/adapters";

type TWeeklyStudyTimeCardProps = {
  /** Called when the settings icon is clicked */
  onSettingsClick?: () => void;
};

export const WeeklyStudyTimeCard: FC<TWeeklyStudyTimeCardProps> = ({ onSettingsClick }) => {
  const { t } = useTranslation();
  const { data, isLoading, isUpdating, weekRangeLabel, targetPercent, goNextWeek, goPrevWeek, isCurrentWeek } = useWeeklyStudyTime();

  if (isLoading || !data) {
    return <WeeklyStudyTimeSkeleton />;
  }

  return (
    <Stack
      sx={{
        bgcolor: background.white,
        borderRadius: rounded.xs,
        border: `1px solid ${divider.high}`,
        py: spacing.sm,
        px: spacing.md,
        gap: spacing.sm,
        position: "relative",
        // 2. Slight blur effect when switching weeks to indicate "Updating"
        opacity: isUpdating ? 0.5 : 1,
        pointerEvents: isUpdating ? "none" : "auto",
        transition: "opacity 0.2s ease",
      }}
    >
      <Stack direction="row" gap={spacing.sm} flexWrap="wrap" justifyContent="center">
        <Stack gap={spacing.xxs} alignItems="center">
          <Stack direction="row" alignItems="flex-start" gap="4px" width="100%" maxWidth="180px" position="relative">
            <Typography sx={{ ...typography.body14Semi, color: textColor.primary, textAlign: "center", flex: 1 }}>
              {t("dashboard.weeklyLearning.title")}
            </Typography>
            <Stack width={spacing.xs} height={1} />
            <IconButton size="small" aria-label="settings" onClick={onSettingsClick} sx={{ position: "absolute", right: "-16px", top: "-4px" }}>
              <IconSetting width={24} height={24} fill={icon.default} />
            </IconButton>
          </Stack>

          <CircularStudyProgress percent={targetPercent} />

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "150%",
              color: textColor.secondary,
              mt: "9px",
            }}
          >
            {t("dashboard.weeklyLearning.target")}
            <Typography component="span" sx={{ ...typography.body14Bold, color: primary[500] }}>
              {data.totalMinutes}
            </Typography>
            /{data.targetMinutes}
            {t("dashboard.weeklyLearning.minuteUnit")}
          </Typography>
        </Stack>

        <Stack flex={1} gap={1} flexWrap="wrap">
          <StudyTimeStatItem
            label={t("dashboard.weeklyLearning.todayStudyTime")}
            value={data.todayMinutes}
            unit={t("dashboard.weeklyLearning.minuteUnit")}
            highlight
          />
          <StudyTimeStatItem
            label={t("dashboard.weeklyLearning.consecutiveDays")}
            value={data.consecutiveDays}
            unit={t("dashboard.weeklyLearning.dayUnit")}
            highlight
          />
          <StudyTimeStatItem
            label={t("dashboard.weeklyLearning.totalStudyDays")}
            value={data.totalStudyDays}
            unit={t("dashboard.weeklyLearning.dayUnit")}
            highlight
          />
        </Stack>
      </Stack>

      <Box sx={{ borderTop: `1px solid ${divider.high}` }} />

      <Stack gap="10px">
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <IconButton size="small" onClick={goPrevWeek} sx={{ color: icon.default }} disabled={isUpdating}>
            <IconChevronLeft fontSize={24} color={icon.default} />
          </IconButton>
          <Typography sx={{ ...typography.body14Semi, color: textColor.secondary }}>{weekRangeLabel}</Typography>
          <IconButton size="small" onClick={goNextWeek} disabled={isCurrentWeek || isUpdating}>
            <IconChevronRight fontSize={24} color={isCurrentWeek ? textColor.disable : icon.default} />
          </IconButton>
        </Stack>

        <WeeklyBarChart days={data.days} dailyTargetMinutes={Math.ceil(data.targetMinutes / 7)} />
      </Stack>
    </Stack>
  );
};
